<template>
  <div
    ref="sectionRef"
    class="relative">
    <GradientSlider
      ref="sliderRef"
      :title="title"
      :copy="copy"
      :images="slidesForLayout"
      :gradient-top="gradientTop"
      :gradient-bottom="gradientBottom"
      :youtube-padding="true">
      <template #slide="{ item, index }">
        <div class="h-full w-full overflow-hidden rounded-lg">
          <YoutubePlayer
            :index="index"
            :active-index="activeIndex"
            :video-id="item.youtube_id || ''"
            :title="item.title || ''"
            :image-path="item.cover"
            @play="onPlay(index)" />
        </div>
      </template>
    </GradientSlider>
  </div>
</template>

<script setup lang="ts">
  import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

  import GradientSlider from '~/components/section/gradient-slider.vue'
  import YoutubePlayer from '~/components/youtube-player.vue'

  type ImageObject = { url?: string; path?: string; permalink?: string; src?: string }
  type ImageInput = string | ImageObject | ImageObject[] | null | undefined

  type YoutubeItem = {
    youtube_id: string
    title: string
    cover_photo?: ImageInput
    thumbnail?: string
  }

  type YoutubeSliderElement = {
    header?: { title?: string; copy?: string }
    title?: string
    copy?: string
    topGradientColor?: string
    bottomGradientColor?: string
    youtube_items?: YoutubeItem[]
  }

  // Accept element coming from page-builder-element (Statamic block)
  const props = withDefaults(defineProps<{ element?: YoutubeSliderElement }>(), {})

  const el = computed(() => props.element)

  function resolveImage(input: ImageInput): string | undefined {
    if (!input) {
      return undefined
    }
    if (typeof input === 'string') {
      return input
    }
    if (Array.isArray(input)) {
      const first = input[0]
      return first?.url || first?.path || first?.permalink || first?.src || undefined
    }
    return input.url || input.path || input.permalink || input.src || undefined
  }

  const youtubeItems = computed<YoutubeItem[]>(() => {
    const items = el.value?.youtube_items
    return Array.isArray(items) ? items : []
  })

  // Provide layout hints to GradientSlider and pass through original fields
  const slidesForLayout = computed(() =>
    youtubeItems.value.map(yt => ({
      alt: yt?.title || 'YouTube video',
      youtube_id: yt?.youtube_id,
      title: yt?.title,
      cover: resolveImage(yt?.cover_photo) || yt?.thumbnail || ''
    }))
  )

  const title = computed<string>(() => el.value?.header?.title || el.value?.title || '')
  const copy = computed<string>(() => el.value?.header?.copy || el.value?.copy || '')

  const gradientTop = computed<string>(() => el.value?.topGradientColor || 'gradient-red')
  const gradientBottom = computed<string>(() => el.value?.bottomGradientColor || 'gradient-red')

  // Control which video is considered active; others will stop
  const activeIndex = ref(0)
  const sliderRef = ref<{ centerSlide: (index: number) => void } | null>(null)
  const sectionRef = ref<HTMLElement | null>(null)
  let observer: IntersectionObserver | null = null

  function onPlay(index: number) {
    activeIndex.value = index
    try {
      sliderRef.value?.centerSlide?.(index)
    } catch {
      // Centering is cosmetic; never block playback on it
    }
  }

  // Stop playback when the section leaves the viewport to avoid stray audio
  onMounted(() => {
    if (typeof window === 'undefined') {
      return
    }
    if (!sectionRef.value) {
      return
    }
    observer = new IntersectionObserver(
      entries => {
        const entry = entries[0]
        const visible = entry?.isIntersecting && entry.intersectionRatio > 0.01
        if (!visible) {
          // Force all players to stop by setting an inactive index
          activeIndex.value = -1
        }
      },
      { threshold: [0, 0.01, 0.1] }
    )
    observer.observe(sectionRef.value)
  })

  onBeforeUnmount(() => {
    if (observer && sectionRef.value) {
      try {
        observer.unobserve(sectionRef.value)
      } catch {
        // Already unobserved; nothing to clean up
      }
      try {
        observer.disconnect()
      } catch {
        // Already disconnected; nothing to clean up
      }
    }
    observer = null
  })
</script>
